import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import {
  Avatar as ShadAvatar,
  AvatarFallback as ShadAvatarFallback,
  AvatarImage as ShadAvatarImage,
} from '@ps/ds/ui/avatar'
import { type VariantProps, cva } from 'class-variance-authority'

const avatarVariants = cva('relative flex shrink-0 overflow-hidden aspect-square', {
  variants: {
    size: {
      xs: 'h-6 w-6 text-xs rounded-md [&_svg]:size-4',
      sm: 'h-9 w-9 text-sm rounded-lg [&_svg]:size-5',
      md: 'h-12 w-12 text-base rounded-xl [&_svg]:size-6',
      lg: 'h-14 w-14 text-base rounded-xl [&_svg]:size-8',
      xl: 'h-[72px] w-[72px] text-2xl rounded-xl [&_svg]:size-10',
    },
    actionable: {
      true: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-foreground focus-visible:ring-offset-background transition-shadow duration-200 ease-in-out hover:shadow-md',
      false: '',
    },
  },
  defaultVariants: {
    size: 'md',
    actionable: false,
  },
})

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof ShadAvatar>,
    VariantProps<typeof avatarVariants> {
  /** The source URL of the avatar image */
  src?: string
  /** Alt text for the avatar image */
  alt?: string
  /** Fallback content to show when image fails to load or is not provided */
  fallback?: React.ReactNode
  /** Additional className for the fallback element */
  fallbackClassName?: string
  /** Delay in ms before showing the fallback. Useful if you want to avoid flickering for fast-loading images. */
  delayMs?: number
  /** Whether this avatar is actionable (clickable/focusable) - enables focus ring */
  actionable?: boolean
  /** Custom children to render inside the Avatar */
  children?: React.ReactNode
}

export const Avatar = React.forwardRef<React.ElementRef<typeof ShadAvatar>, AvatarProps>(
  (
    {
      src,
      alt,
      fallback,
      size = 'md',
      actionable = false,
      delayMs,
      className,
      fallbackClassName,
      children,
      ...props
    },
    ref,
  ) => {
    // Generate initials from alt text if no fallback is provided
    const defaultFallback = React.useMemo(() => {
      if (fallback) return fallback
      if (!alt) return '?'

      const words = alt.trim().split(/\s+/)
      if (words.length >= 2) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase()
      }
      return alt.slice(0, 2).toUpperCase()
    }, [alt, fallback])

    if (children) {
      return (
        <ShadAvatar
          ref={ref}
          className={cn(avatarVariants({ size, actionable }), className)}
          data-testid="avatar"
          {...props}>
          {children}
        </ShadAvatar>
      )
    }

    return (
      <ShadAvatar
        ref={ref}
        className={cn(avatarVariants({ size, actionable }), className)}
        data-testid="avatar"
        {...props}>
        {src && <AvatarImage src={src} alt={alt} data-testid="avatar-image" />}
        <AvatarFallback
          delayMs={delayMs}
          data-testid="avatar-fallback"
          className={fallbackClassName}>
          {defaultFallback}
        </AvatarFallback>
      </ShadAvatar>
    )
  },
)

Avatar.displayName = 'Avatar'

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof ShadAvatarFallback>,
  React.ComponentPropsWithoutRef<typeof ShadAvatarFallback>
>(({ className, ...props }, ref) => {
  return (
    <ShadAvatarFallback
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center font-medium bg-muted text-muted-foreground rounded-none',
        className,
      )}
      {...props}
    />
  )
})

AvatarFallback.displayName = 'AvatarFallback'

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof ShadAvatarImage>,
  React.ComponentPropsWithoutRef<typeof ShadAvatarImage>
>(({ className, ...props }, ref) => {
  return (
    <ShadAvatarImage
      ref={ref}
      className={cn('aspect-square h-full w-full object-cover', className)}
      {...props}
    />
  )
})

AvatarImage.displayName = 'AvatarImage'

export { AvatarImage, AvatarFallback }
